import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, PenLine, Pencil, Plus, ShieldCheck, TriangleAlert, UserPlus } from 'lucide-react';
import {
  useListStaffQuery,
  useCreateStaffMutation,
  useListGroupsQuery,
  useUpdateStaffMutation,
  useDeactivateStaffMutation,
  getApiError,
  type StaffMember,
  type CreateStaffData,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { EmptyState } from '@/components/ui/EmptyState';
import { Avatar } from '@/components/ui/Avatar';
import { StaffImagesDialog } from './StaffImagesDialog';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  One schema for creating and editing, with the password the only difference:
  required for a new account, and on an existing one an empty box means "leave
  it alone" rather than "set it to nothing".
*/
const staffSchema = z.object({
  fullName: z.string().min(2, 'Full name is required'),
  email: z.string().email('A valid email address is required'),
  password: z.string().optional(),
  role: z.enum(['gate_officer', 'teacher', 'institution_admin']),
  /* Teachers only. Checkboxes, built below from the institution's groups. */
  groupIds: z.array(z.string()).default([]),
});
type StaffForm = z.infer<typeof staffSchema>;

const dateFmt = new Intl.DateTimeFormat('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });

export function GateOfficersPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const [showModal, setShowModal] = useState(false);
  /*
    The ID, not the row.

    Holding the row meant holding a SNAPSHOT: uploading a signature refreshed
    the staff list underneath, but the dialog carried on showing the copy it
    was opened with, so the preview stayed empty and the upload looked like it
    had failed. Looking the member up from the live list each render means the
    dialog shows what was just saved.
  */
  const [imagesForId, setImagesForId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  /* Set while correcting an existing record; null while adding one. */
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: staff = [], isLoading, isError, error } = useListStaffQuery(institutionId, {
    skip: !institutionId,
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<StaffForm>({
    resolver: zodResolver(staffSchema),
    defaultValues: { role: 'gate_officer', groupIds: [] },
  });

  /* Watched, so the classes appear the moment somebody picks Teacher. */
  const role = watch('role');
  const selectedGroups = watch('groupIds') ?? [];
  const { data: groups = [] } = useListGroupsQuery(institutionId, { skip: !institutionId });

  const [createStaff, createMutation] = useCreateStaffMutation();
  const [updateStaff, updateMutation] = useUpdateStaffMutation();
  const [deactivateStaff, deactivateMutation] = useDeactivateStaffMutation();

  const close = () => {
    setShowModal(false);
    setEditingId(null);
    setShowPassword(false);
    reset();
  };

  const openForCreate = () => {
    setEditingId(null);
    reset({ fullName: '', email: '', password: '', role: 'gate_officer', groupIds: [] });
    setShowModal(true);
  };

  const openForEdit = (member: StaffMember) => {
    setEditingId(member.id);
    reset({
      fullName: member.fullName,
      email: member.email,
      /* Blank on purpose — see the note on the schema. */
      password: '',
      role: member.role,
      groupIds: member.groupIds ?? [],
    });
    setShowModal(true);
  };

  const onSubmit = async (data: StaffForm) => {
    try {
      if (editingId) {
        await updateStaff({
          institutionId,
          userId: editingId,
          data: {
            fullName: data.fullName,
            email: data.email,
            role: data.role,
            groupIds: data.groupIds,
            /* Only sent when something was typed, so saving a name change does
               not reset somebody's password to an empty string. */
            ...(data.password ? { password: data.password } : {}),
          },
        }).unwrap();
      } else {
        if (!data.password || data.password.length < 8) {
          setError('password', { message: 'At least 8 characters' });
          return;
        }
        await createStaff({ institutionId, data: data as CreateStaffData }).unwrap();
      }
      close();
    } catch {
      /* Surfaced inline from the mutation error — the dialog stays open so the
         typed values are not lost. */
    }
  };

  const roleLabel = (role: StaffMember['role']) =>
    role === 'gate_officer' ? 'Gate officer' : role === 'teacher' ? 'Teacher' : 'Institution admin';

  const officers = staff.filter((s) => s.role === 'gate_officer');
  const teachers = staff.filter((s) => s.role === 'teacher');
  const admins = staff.filter((s) => s.role === 'institution_admin');

  const columns: Column<StaffMember>[] = [
    {
      key: 'fullName',
      header: 'Name',
      render: (s) => (
        <div className="flex items-center gap-2.5">
          <Avatar src={s.photoUrl} name={s.fullName} size={26} zoomable caption={roleLabel(s.role)} />
          <span className="font-semibold text-foreground">{s.fullName}</span>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (s) => <span className="text-muted-foreground">{s.email}</span>,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (s) => (
        <Mark tone={s.isActive ? 'success' : 'neutral'}>{s.isActive ? 'Active' : 'Inactive'}</Mark>
      ),
      className: 'w-[100px]',
    },
    {
      key: 'signature',
      header: 'Signature',
      render: (s) =>
        /* Visible before the end of term rather than on the day forty report
           cards are printed and two of them come out unsigned. */
        s.hasSignature ? (
          <Mark tone="success">On file</Mark>
        ) : (
          <Mark tone="neutral">None</Mark>
        ),
      className: 'w-[110px] max-sm:hidden',
    },
    {
      key: 'createdAt',
      header: 'Added',
      render: (s) => (
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {s.createdAt ? dateFmt.format(new Date(s.createdAt)) : '—'}
        </span>
      ),
      className: 'w-[130px] max-sm:hidden',
    },
    {
      key: 'actions',
      header: '',
      align: 'num',
      render: (s) => (
        <div className="flex justify-end gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Edit ${s.fullName}`}
            title="Name, email, role, classes and password"
            onClick={() => openForEdit(s)}
          >
            <Pencil size={13} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`${s.fullName}'s photo and signature`}
            title="Passport photo and the signature printed on report cards"
            onClick={() => setImagesForId(s.id)}
          >
            <PenLine size={13} />
          </Button>
          {/*
            A two-way switch, because deactivating used to be a one-way door —
            a staff member switched off had no way back short of the database.
          */}
          {s.isActive ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={deactivateMutation.isLoading}
              onClick={() => void deactivateStaff({ institutionId, userId: s.id })}
              className="text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
            >
              Deactivate
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              loading={updateMutation.isLoading}
              onClick={() => void updateStaff({ institutionId, userId: s.id, data: { isActive: true } })}
            >
              Reactivate
            </Button>
          )}
        </div>
      ),
      className: 'w-[230px]',
    },
  ];

  const addButton = (
    <Button variant="primary" icon={<Plus size={13} />} onClick={openForCreate}>
      Add staff
    </Button>
  );

  /* Two tables rather than one with a Role column: the question this screen
     answers is "who can open the gate app", and that is a different list from
     "who can administer the institution". A role column makes the reader filter
     it themselves every time. */
  const section = (title: string, members: StaffMember[], emptyNote: string) => (
    <div className={cn(styles.panel, 'overflow-clip')}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <h2 className={styles.sectionTitle}>{title}</h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.06em] tabular-nums text-muted-foreground">
          {members.length}
        </span>
      </div>
      <DataTable
        columns={columns}
        rows={members}
        rowKey={(s) => s.id}
        emptyTitle={emptyNote}
        emptyDescription="Add one with the button above."
      />
    </div>
  );

  return (
    <PageLayout
      title="Staff"
      subtitle="Who can verify codes, and who can administer the institution"
      breadcrumbs={[{ label: 'People' }, { label: 'Staff' }]}
      actions={addButton}
      contained={false}
    >
      {isLoading || isError || staff.length === 0 ? (
        <div className={styles.panel}>
          {isError ? (
            <EmptyState
              icon={<TriangleAlert size={17} />}
              title="Could not load staff"
              description={getApiError(error)}
            />
          ) : isLoading ? (
            <DataTable columns={columns} rows={[]} rowKey={(s) => s.id} loading />
          ) : (
            <EmptyState
              icon={<ShieldCheck size={17} />}
              title="No staff yet"
              description="Gate officers verify pickup codes at dismissal. Teachers can only read the dismissal board. Institution admins manage students, groups and reports."
              action={
                <Button variant="primary" icon={<UserPlus size={13} />} onClick={openForCreate}>
                  Add the first staff member
                </Button>
              }
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {section('Gate officers', officers, 'No gate officers yet')}
          {section('Teachers', teachers, 'No teachers yet — add one to put the dismissal board on a classroom screen')}
          {section('Institution admins', admins, 'No institution admins yet')}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={close}
        title={editingId ? 'Edit staff member' : 'Add staff member'}
        description={
          editingId
            ? 'Leave the password blank to keep the one they already have.'
            : 'They sign in with this email and password on the gate app or this dashboard.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              form="add-staff"
              type="submit"
              loading={createMutation.isLoading || updateMutation.isLoading}
            >
              {editingId ? 'Save changes' : 'Add staff member'}
            </Button>
          </>
        }
      >
        <form id="add-staff" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3" noValidate>
          <Field label="Full name" htmlFor="staff-name" error={errors.fullName?.message} required>
            <Input id="staff-name" placeholder="Kwame Asante" invalid={!!errors.fullName} {...register('fullName')} />
          </Field>

          <Field label="Email address" htmlFor="staff-email" error={errors.email?.message} required>
            <Input
              id="staff-email"
              type="email"
              placeholder="kwame@school.edu.gh"
              invalid={!!errors.email}
              {...register('email')}
            />
          </Field>

          <Field
            label={editingId ? 'New password' : 'Password'}
            htmlFor="staff-password"
            error={errors.password?.message}
            hint={
              editingId
                ? 'Only if they are locked out. Leave blank to keep the current one — setting a new one signs them out everywhere.'
                : 'At least 8 characters. Ask them to change it after their first sign-in.'
            }
            required={!editingId}
          >
            <div className="relative">
              <Input
                id="staff-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Minimum 8 characters"
                invalid={!!errors.password}
                className="pr-8"
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </Field>

          <Field label="Role" htmlFor="staff-role" required>
            <Select id="staff-role" {...register('role')}>
              <option value="gate_officer">Gate officer — verifies codes and releases children</option>
              <option value="teacher">Teacher — register, marks and the dismissal board for their own classes</option>
              <option value="institution_admin">Institution admin — full dashboard access</option>
            </Select>
          </Field>

          {/*
            ── A teacher's classes ───────────────────────────────────────────

            These were promised by a comment on the schema and never built, so
            a teacher created here got none — and everything scoped to a
            teacher's classes (the register, mark entry, the dismissal board's
            "your class" marking) silently had nothing to work on. Worse, the
            edit form then sent an empty list on every save, which quietly
            cleared classes somebody had set in the database.

            Shown only for a teacher, because for any other role the field
            would imply a permission the role does not have.
          */}
          {role === 'teacher' && (
            <Field
              label="Classes"
              htmlFor="staff-groups"
              hint={
                groups.length === 0
                  ? 'No classes exist yet — create them under Groups first.'
                  : 'What they can take the register for, enter marks for, and see on the dismissal board.'
              }
            >
              <div id="staff-groups" className="flex flex-wrap gap-1.5">
                {groups.map((g) => {
                  const on = selectedGroups.includes(g._id);
                  return (
                    <button
                      key={g._id}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setValue(
                          'groupIds',
                          on ? selectedGroups.filter((x) => x !== g._id) : [...selectedGroups, g._id],
                          { shouldDirty: true },
                        )
                      }
                      className={cn(
                        'rounded-md border px-2.5 py-1 text-[11.5px] font-semibold transition-colors',
                        on
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          {(createMutation.error || updateMutation.error) && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]"
            >
              <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
              {getApiError(createMutation.error ?? updateMutation.error)}
            </div>
          )}
        </form>
      </Modal>

      <StaffImagesDialog
        open={imagesForId !== null}
        onClose={() => setImagesForId(null)}
        institutionId={institutionId}
        member={staff.find((m) => m.id === imagesForId) ?? null}
      />

    </PageLayout>
  );
}
